import { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/shared/components/ui';
import { authService } from '../api/authService';
import { CONFIGURACION_SESION } from '@/config/configuracion';

/**
 * Hook para gestionar sesión de usuario
 * 
 * Responsabilidades:
 * - Verificar expiración de token cada X segundos (configuración.INTERVALO_VERIFICACION_TOKEN)
 * - Mostrar modal de advertencia X minutos antes de expirar (configuración.ADVERTENCIA_ANTES_EXPIRACION)
 * - Auto-logout cuando el token expira
 * - Renovación manual de token desde el modal
 */
export const useGestorSesion = () => {
    const { tokenExpiracion, logout, isAuthenticated, login } = useAuthStore();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [showWarning, setShowWarning] = useState(false);
    const [minutesRemaining, setMinutesRemaining] = useState<number | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const hasExpiredRef = useRef(false);
    const warningDismissedRef = useRef(false);

    useEffect(() => {
        if (!isAuthenticated || !tokenExpiracion) {
            setShowWarning(false);
            setMinutesRemaining(null);
            hasExpiredRef.current = false;
            warningDismissedRef.current = false;
            return;
        }

        const checkExpiration = () => {
            const now = new Date();
            const expiration = new Date(tokenExpiracion);
            const msRemaining = expiration.getTime() - now.getTime();
            const minutes = Math.floor(msRemaining / 60000);

            setMinutesRemaining(minutes);

            // Token expirado
            if (msRemaining <= 0 && !hasExpiredRef.current) {
                hasExpiredRef.current = true;
                setShowWarning(false);

                // ✅ FIX: Llamar endpoint logout para registrar evento en BD
                authService.logout().catch((err) => {
                    console.error('Error al registrar logout en BD:', err);
                });

                logout();  // Logout local (limpia store)

                showToast({
                    type: 'error',
                    message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
                });

                navigate('/login', { replace: true });
                return;
            }

            // Mostrar modal X minutos antes de expirar (configurado en configuracion.ts)
            const minutosAdvertencia = CONFIGURACION_SESION.ADVERTENCIA_ANTES_EXPIRACION;
            if (minutes <= minutosAdvertencia && minutes > 0 && !warningDismissedRef.current) {
                console.log('✅ [SessionManager] MOSTRANDO MODAL - minutos:', minutes);
                setShowWarning(true);
            } else if (minutes > minutosAdvertencia) {
                // Reset dismissed cuando hay más de X min (nuevo token)
                warningDismissedRef.current = false;
                setShowWarning(false);
            }
        };

        checkExpiration();
        const interval = setInterval(checkExpiration, CONFIGURACION_SESION.INTERVALO_VERIFICACION_TOKEN);

        return () => clearInterval(interval);
    }, [tokenExpiracion, isAuthenticated, logout, navigate, showToast]);  // ✅ FIX: Removido showWarning


    const dismissWarning = () => {
        warningDismissedRef.current = true;
        setShowWarning(false);
    };

    const handleRenewSession = async () => {
        try {
            setIsRefreshing(true);

            const response = await authService.refreshToken();

            if (!response.success || !response.data) {
                throw new Error(response.message || 'Error al renovar token');
            }

            // Actualizar store con nuevo token
            login(
                {
                    id: response.data.usuario.id,
                    userId: response.data.usuario.usuarioId,
                    email: response.data.usuario.correo,
                    name: response.data.usuario.nombre,
                    roles: response.data.usuario.roles,
                    cuentaActiva: response.data.usuario.cuentaActiva,
                },
                response.data.token,
                response.data.tokenExpiracion,
                false
            );


            showToast({
                type: 'success',
                message: 'Sesión renovada exitosamente. Continuarás conectado por 1 hora más.',
            });

            // Cerrar modal y resetear flags
            setShowWarning(false);
            warningDismissedRef.current = false;

        } catch (error) {
            showToast({
                type: 'error',
                message: 'Error al renovar sesión. Por favor, inicia sesión nuevamente.',
            });

            logout();
            navigate('/login');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleLogoutNow = async () => {
        setShowWarning(false);

        // ✅ FIX: Llamar endpoint logout para registrar evento
        try {
            await authService.logout();
        } catch (error) {
            console.error('Error al registrar logout:', error);
        }

        await logout();  // Logout local
        navigate('/login');
    };

    return {
        showWarning,
        minutesRemaining,
        dismissWarning,
        handleRenewSession,
        handleLogoutNow,
        isRefreshing
    };
};
